"use client"

import * as React from "react"

import { cn } from "../../lib/utils"

/**
 * v5.2 Switch — native `<input type="checkbox">` visually re-drawn
 * via `.ac-switch` CSS. Label sits left, track + thumb right.
 *
 * Usage:
 *   <Switch checked onCheckedChange={setOn}>Email reminders</Switch>
 *
 * Prefer Switch for live settings (apply immediately). Use Checkbox
 * when the change commits on form submit.
 */
export interface SwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  onCheckedChange?: (checked: boolean) => void
  children?: React.ReactNode
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, disabled, onCheckedChange, onChange, children, ...props }, ref) => (
    <label
      className={cn(
        "ac-switch",
        children != null && "w-full justify-between",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {children != null && <span className="label-text">{children}</span>}
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        onChange={(e) => {
          onCheckedChange?.(e.target.checked)
          onChange?.(e)
        }}
        {...props}
      />
      <span className="track">
        <span className="thumb" />
      </span>
    </label>
  )
)
Switch.displayName = "Switch"

export { Switch }
